import { PackagePanelView } from '../view/PackagePanelView';
import { templatePackagePanel } from '../view/template';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { PackagePanelWebComponent } from '../component/PackagePanelWebComponent';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import _ from 'lodash';
import { PackageEvent } from '../types/PackageEvent';
import {
  OnCompanySelect,
  OnOperationModeSelect,
  PackageGeneralEvent,
} from '../../common/packageGeneralForm/types/PackageGeneralEvent';
import { PackagePanelRepository } from '../dataProvider/PackagePanelRepository';
import { OnPackagePanelClose, OnPackagePanelMaybeClose } from '../../common/events/PackagePanelEvents';
import { PackageActionContext } from '../../common/types/PackageActionContext';
import { PackageViewData } from '../types/PackageViewData';
import { translateFn } from '../../../utils/translator.helper';
import { showToast } from '../../../tagsManager/helpers/commonFunctions';
import { combineLatest, Subscription } from 'rxjs';
import { DeployerGridEvents } from '../../common/deployerGrid/types';
import {
  OnCustomScanModeOptionChanged,
  OnScanModeRadioSelectionChanged,
  PackageScanModesEvent,
} from '../../common/packageScanModesForm/types/PackageScanModesEvent';
import { ScanModes } from '../../common/packageScanModesForm/types/ScanModes';
import { Feature } from '@nirvana/shared/constants';
import { ModulesData, PackageData } from '../../types/package-data';
import { PackageType } from '../../packagesGrid/types';
import { PackageSettingsEvent } from '../../common/packageSettingsForm/types/PackageSettingsEvent';
import {
  OnRemoveCompetitorsSelect,
  PackageModulesEvent,
} from '../../common/packageModulesForm/types/PackageModulesEvent';
import { MonthlyProductType } from '../../../license/types/MonthlyProductType';
import { DataProviderType } from '../../../utils/request';
import { KitData } from '../../downloadKitButtonSelector/constants';
import { DeepPartial } from 'typeorm';

export class PackagePanelController extends BaseWebComponentController {
  public component: PackagePanelWebComponent;
  public dataRepository: PackagePanelRepository;
  public view: PackagePanelView;
  public viewConstructor: typeof PackagePanelView;
  private packageData: PackageData;
  private availableFeatures: Feature[];
  private companyId: string | undefined;
  private validSubscription: Subscription | null;

  constructor(component: PackagePanelWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: PackagePanelView,
      viewTemplate: templatePackagePanel,
      dataRepositoryConstructor: PackagePanelRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$wrapper,
        event: PackageEvent.OnViewInit,
        callback: this.showPackageDetailsForm,
      },
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.savePackage,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.emitMaybeClosePackageWindowEvent,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageGeneralEvent.OnCompanySelect,
        callback: this.onCompanySelect,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageGeneralEvent.OnOperationModeSelect,
        callback: this.onOperationModeSelect,
      },
      {
        element: this.view.dom.$packageModulesForm,
        event: DeployerGridEvents.RELAY_CHECKBOX_EVENT,
        callback: this.setRelayCheckboxState,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageModulesEvent.OnRemoveCompetitorsSelect,
        callback: this.handleRemoveCompetitorsCheckbox,
      },
      {
        element: this.view.dom.$packageDeployerForm,
        event: DeployerGridEvents.CONNECT_TO_SELECTED_EVENT,
        callback: this.disableRelayCheckboxModule,
      },
      {
        element: this.view.dom.$packageScanModesForm,
        event: PackageScanModesEvent.OnCustomScanModeOptionChanged,
        callback: this.onCustomScanModeChange,
      },
      {
        element: this.view.dom.$packageScanModesForm,
        event: PackageScanModesEvent.OnScanModeRadioSelectionChanged,
        callback: this.onScanModeRadioSelectionChange,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageScanModesEvent.OnInit,
        callback: this.onCustomScanModeFinishInit,
      },
    ];
  }

  private disableRelayCheckboxModule(event: WebComponentEvent): void {
    if (this.packageData?.type === PackageType.Frat || this.packageData?.type === PackageType.RelayForFrat) {
      return;
    }
    this.view.dom.$packageModulesForm.disableRelayCheckboxModule = event.detail.selected;
  }

  private async setRelayCheckboxState(event: WebComponentEvent): Promise<void> {
    await this.view.dom.$packageDeployerForm.setRelayCheckboxState(event.detail.selected);
  }

  private handleRemoveCompetitorsCheckbox(event: OnRemoveCompetitorsSelect): void {
    this.component.toggleVisibilityRemoveCompetitorInfo(event.detail.isRemoveCompetitorsChecked);
  }

  private async onCustomScanModeChange(ev: OnCustomScanModeOptionChanged): Promise<void> {
    this.view.dom.$packageScanModesForm.useDefaultValues = false;
    await this.view.dom.$packageSecurityServerAssignmentGrid.showSecurityServerAssignmentSection(
      ev.detail.hasCentralScan,
      this.companyId
    );
  }

  private async onScanModeRadioSelectionChange(ev: OnScanModeRadioSelectionChanged): Promise<void> {
    const scanType: ScanModes = ev.detail.selectedValue;
    const hasCustomModeOptions = this.view.dom.$packageScanModesForm.hasCustomModeOptions(scanType);

    await this.view.dom.$packageSecurityServerAssignmentGrid.showSecurityServerAssignmentSection(
      hasCustomModeOptions,
      this.companyId
    );
  }

  private onOperationModeSelect(event: OnOperationModeSelect): void {
    const productType: MonthlyProductType = event.detail.productType;
    this.view.dom.$packageScanModesForm.toggleVisibilityBasedOnProductType(productType);
    this.component.toggleVisibilityRemoveCompetitorInfo(productType !== MonthlyProductType.BitdefenderEdr);
    this.view.dom.$packageMiscellaneousForm.showSubmitQuarantineSectionByOperationMode(productType);
  }

  private async onCustomScanModeFinishInit(ev: WebComponentEvent): Promise<void> {
    const isVisible = this.view.dom.$packageScanModesForm.hasCentralScan(ev.detail);
    await this.view.dom.$packageSecurityServerAssignmentGrid.showSecurityServerAssignmentSection(
      isVisible,
      this.companyId
    );
  }

  private async onCompanySelect(event: OnCompanySelect): Promise<void> {
    this.view.dom.$packageScanModesForm.useDefaultValues =
      typeof this.companyId === 'string' && this.companyId !== event.detail.companyId;
    this.companyId = event.detail.companyId;

    this.view.setLoadingState();

    const availableFeatures = await this.dataRepository.getFeaturesByCompany({ companyId: event.detail.companyId });
    this.availableFeatures = availableFeatures;

    await this.initModulesSection(event, availableFeatures);
    this.initMiscellaneousSection(event.detail.companyId);
    this.initScanModeSection(availableFeatures);
    await this.initDeployerSection(event);
    await this.initUseProxySection(event);
    this.reloadSettingsSection(event.detail.companyId).then().catch(console.error);
    this.view.dom.$packageScanModesForm.useDefaultValues = true;

    this.view.removeLoadingState();
  }

  private async showPackageDetailsForm(): Promise<void> {
    this.view.setLoadingState();
    this.setValidation();
    this.setSettingsUseCustomFolderEventListener();
    this.view.removeLoadingState();
  }

  private setValidation(): void {
    if (this.component.isStaging) {
      this.setStagingValidationSubscription();
      return;
    }

    this.setDefaultValidationSubscription();
  }

  private setDefaultValidationSubscription(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = combineLatest([
      this.view.dom.$packageGeneralForm.valid$,
      this.view.dom.$packageModulesForm.valid$,
      this.view.dom.$packageDeployerForm.valid$,
      this.view.dom.$packageUseProxyForm.valid$,
      this.view.dom.$packageSecurityServerAssignmentGrid.valid$,
      this.view.dom.$packageSettingsForm.valid$,
    ]).subscribe({
      next: ([
        packageGeneralValid,
        packageModulesValid,
        packageDeployerValid,
        packageUseProxyFormValid,
        packageSecurityServerAssignmentValid,
        packageSettingsFormValid,
      ]) => {
        this.view.dom.$saveButton.disabled = !(
          packageGeneralValid &&
          packageModulesValid &&
          packageDeployerValid &&
          packageUseProxyFormValid &&
          packageSecurityServerAssignmentValid &&
          packageSettingsFormValid
        );
      },
    });
  }

  private setStagingValidationSubscription(): void {
    this.validSubscription?.unsubscribe();

    this.validSubscription = combineLatest([
      this.view.dom.$downloadTypeForm.valid$,
      this.view.dom.$packageGeneralForm.valid$,
      this.view.dom.$packageModulesForm.valid$,
      this.view.dom.$packageDeployerForm.valid$,
      this.view.dom.$packageUseProxyForm.valid$,
      this.view.dom.$packageSecurityServerAssignmentGrid.valid$,
      this.view.dom.$packageSettingsForm.valid$,
    ]).subscribe({
      next: ([
        downloadTypeValid,
        packageGeneralValid,
        packageModulesValid,
        packageDeployerValid,
        packageUseProxyFormValid,
        packageSecurityServerAssignmentValid,
        packageSettingsFormValid,
      ]) => {
        this.view.dom.$saveButton.disabled = !(
          downloadTypeValid &&
          packageGeneralValid &&
          packageModulesValid &&
          packageDeployerValid &&
          packageUseProxyFormValid &&
          packageSecurityServerAssignmentValid &&
          packageSettingsFormValid
        );
      },
    });
  }

  /**
   * We need to set this  only after all other sections are  started
   * @private
   */
  private setSettingsUseCustomFolderEventListener() {
    this.view.dom.$wrapper.addEventListener(
      PackageSettingsEvent.OnUseCustomFolderChange,
      (event: WebComponentEvent) => {
        this.onSettingsUseCustomFolderChange(event.detail.checked);
      }
    );
  }

  /**
   * We need to update the validSubscription with $settingsTargets
   * We don't want to start $settingsTargets component is not needed
   * @param isChecked
   * @private
   */
  private onSettingsUseCustomFolderChange(isChecked: boolean): void {
    this.setValidation();
  }

  private async savePackage() {
    this.view.dom.$saveButton.showSpinner = true;
    this.view.dom.$saveButton.disabled = true;

    this.component.isStaging ? this.downloadPackage() : await this.createPackage();
  }

  private async createPackage(): Promise<void> {
    if (this.component.getPackageType() === PackageType.Frat && this.view.dom.$packageModulesForm.isRelayForFrat) {
      this.component.setPackageType(PackageType.RelayForFrat);
    }

    let savePackageResult,
      toastMessage,
      toastSeverity = ToastSeverity.ERROR;

    if (this.component.formContext === PackageActionContext.Create) {
      savePackageResult = await this.createPackageRecord();

      toastMessage = savePackageResult.success
        ? translateFn('Package created successfully.')
        : translateFn('Failed to create package.');
    } else if (PackageActionContext.Edit) {
      savePackageResult = await this.updatePackageRecord();

      toastMessage = savePackageResult.success
        ? translateFn('Package updated successfully.')
        : translateFn('Failed to update package.');
    }

    if (savePackageResult.success) {
      toastSeverity = ToastSeverity.SUCCESS;
    }

    showToast(toastSeverity, toastMessage);

    this.view.dom.$saveButton.showSpinner = false;
    this.view.dom.$saveButton.disabled = false;

    if (savePackageResult.success) {
      this.emitClosePackageWindowEvent();
    }
  }

  private downloadPackage(): void {
    const selectedKit: KitData = this.dependencies.selectedKit();
    const packageGeneralData: DeepPartial<PackageData> = this.view.dom.$packageGeneralForm.value;
    const packageRecord = _.merge(
      this.view.dom.$packageModulesForm.value,
      this.view.dom.$packageScanModesForm.value,
      this.view.dom.$packageSecurityServerAssignmentGrid.value,
      this.view.dom.$packageMiscellaneousForm.value,
      this.view.dom.$packageUseProxyForm.value,
      this.view.dom.$packageSettingsForm.value,
      this.view.dom.$downloadTypeForm.value,
      this.view.dom.$packageDeployerForm.value,
      {
        language: packageGeneralData.language,
        type: this.component.getPackageType(),
      }
    );

    this.downloadKit(packageRecord, selectedKit);
    this.emitClosePackageWindowEvent();
  }

  private downloadKit(packageRecord: DeepPartial<PackageData>, selectedKit: KitData): void {
    const record = {
      packageData: packageRecord,
      product: selectedKit.productInfo,
      kitVersion: selectedKit.version,
    };
    const url: string = app.getDataProviderUpload(DataProviderType.CORE, 'Packages', 'downloadKitForStaging');
    const form: HTMLFormElement = document.createElement('form');
    const params: HTMLInputElement = document.createElement('input');
    const paramsEncoded: string = JSON.stringify(record);
    params.setAttribute('name', 'params');
    params.setAttribute('value', paramsEncoded);
    form.appendChild(params);
    form.setAttribute('method', 'POST');
    form.setAttribute('action', url);
    form.setAttribute('id', 'downloadKitForStaging');
    document.body.appendChild(form);

    form.submit();
  }

  private async createPackageRecord(): Promise<{ success: boolean; errorMessage?: string }> {
    const packageRecord = _.merge(
      this.view.dom.$packageGeneralForm.value,
      this.view.dom.$packageModulesForm.value,
      this.view.dom.$packageScanModesForm.value,
      this.view.dom.$packageSecurityServerAssignmentGrid.value,
      this.view.dom.$packageMiscellaneousForm.value,
      this.view.dom.$packageDeployerForm.value,
      this.view.dom.$packageUseProxyForm.value,
      this.view.dom.$packageSettingsForm.value,
      {
        kitType: 0,
        type: this.component.getPackageType(),
      }
    );

    return this.dataRepository.addPackage(packageRecord);
  }

  private async updatePackageRecord(): Promise<{ success: boolean }> {
    const packageRecord = _.merge(
      this.view.dom.$packageGeneralForm.value,
      this.view.dom.$packageModulesForm.value,
      this.view.dom.$packageScanModesForm.value,
      this.view.dom.$packageSecurityServerAssignmentGrid.value,
      this.view.dom.$packageMiscellaneousForm.value,
      this.view.dom.$packageDeployerForm.value,
      this.view.dom.$packageUseProxyForm.value,
      this.view.dom.$packageSettingsForm.value,
      {
        _id: this.packageData._id,
        type: this.component.getPackageType(),
        userId: this.packageData.userId,
        kitType: 0,
      }
    );

    return this.dataRepository.updatePackage(packageRecord);
  }

  private emitMaybeClosePackageWindowEvent(): void {
    this.component.dispatchEvent(new OnPackagePanelMaybeClose());
  }

  private emitClosePackageWindowEvent(): void {
    this.component.dispatchEvent(new OnPackagePanelClose());
  }

  private async initModulesSection(event: OnCompanySelect, companyFeatures): Promise<void> {
    this.view.dom.$packageModulesForm.controller.view.formGroup.setDisabled(false);
    if (this.view.dom.$packageDeployerForm.isRelay) {
      this.view.dom.$packageModulesForm.disableRelayCheckboxModule = true;
    }

    this.view.dom.$packageModulesForm.controller.setAvailableFeatures(companyFeatures);
    this.view.dom.$packageModulesForm.controller.setOperationMode();

    if (this.packageData && this.packageData.company.id === event.detail.companyId) {
      const modulesData: ModulesData = {
        productType: this.packageData.settings.productType,
        modules: {
          ...this.view.dom.$packageModulesForm.controller.getFalseValueModules(),
          ...this.packageData.settings.modules,
        },
        removeCompetitors: this.packageData.settings.removeCompetitors,
      };
      this.view.dom.$packageModulesForm.packageType = this.packageData.type;
      this.view.dom.$packageModulesForm.packageData = modulesData;
    }

    this.view.dom.$packageModulesForm.controller.setMailServersCheckbox(
      this.view.dom.$packageModulesForm.operationMode
    );
    this.view.dom.$packageModulesForm.controller.setPatchManagementServerCheckbox();
  }

  private initMiscellaneousSection(companyId: string): void {
    if (companyId !== this.view.dom.$packageSettingsForm.companyId) {
      this.view.dom.$packageMiscellaneousForm.controller.view.formGroup.setDisabled(false);
      if (!this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_FRAT_VISIBLE)) {
        this.view.dom.$packageMiscellaneousForm.resetToDefaultValues();
      }
    }

    const productType = this.view.dom.$packageModulesForm.operationMode;
    this.view.dom.$packageMiscellaneousForm.showSubmitQuarantineSectionByOperationMode(productType);
  }

  /**
   * This method enables the Scan Mode section if the package is not FRAT (for FRAT it will remain disable)
   * and if it is on Create context. For Edit we'll not enable the section here because the package data is set before
   * this method is called and it will enable all the fields (even the ones that have to be disabled).
   * Scan Modes is set to automatic if the context is Create or if the company is changed.
   *
   * @param {Feature[]} availableFeatures
   * @private
   */
  private initScanModeSection(availableFeatures: Feature[]): void {
    if (
      !this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_FRAT_VISIBLE) &&
      [PackageType.Frat, PackageType.RelayForFrat].indexOf(this.component.getPackageType()) === -1 &&
      this.component.formContext === PackageActionContext.Create
    ) {
      this.view.dom.$packageScanModesForm.controller.view.formGroup.setDisabled(false);
    }
    this.view.dom.$packageScanModesForm.setFeatures(availableFeatures);
    if (
      this.component.formContext === PackageActionContext.Create ||
      this.view.dom.$packageScanModesForm.useDefaultValues
    ) {
      this.view.dom.$packageScanModesForm.setScanModeAuto(availableFeatures);
    }
  }

  private async initDeployerSection(event: OnCompanySelect): Promise<void> {
    if (
      this.view.dom.$packageDeployerForm.controller.view.formGroup &&
      this.view.dom.$packageDeployerForm.formContext === PackageActionContext.Create
    ) {
      this.view.dom.$packageDeployerForm.controller.view.formGroup.setDisabled(false);
    }
    if (this.view.dom.$packageDeployerForm.getCompanyId() !== event.detail.companyId) {
      this.view.dom.$packageDeployerForm.setCompanyId(event.detail.companyId);
      await this.view.dom.$packageDeployerForm.companyChangeHandler();
    }
  }

  private async initUseProxySection(event: OnCompanySelect): Promise<void> {
    if (
      this.view.dom.$packageUseProxyForm.controller.view.formGroup &&
      this.view.dom.$packageUseProxyForm.formContext === PackageActionContext.Create
    ) {
      this.view.dom.$packageUseProxyForm.controller.view.formGroup.setDisabled(false);
    }
    if (this.view.dom.$packageUseProxyForm.getCompanyId() !== event.detail.companyId) {
      this.view.dom.$packageUseProxyForm.setCompanyId(event.detail.companyId);
      await this.view.dom.$packageUseProxyForm.companyChangeHandler();
    }
  }

  /**
   * Reload settings section on company change
   * On add on first company change
   * On edit reset packageData -> use default after company change
   * @param companyId
   * @private
   */
  private async reloadSettingsSection(companyId: string): Promise<void> {
    if (companyId !== this.view.dom.$packageSettingsForm.companyId) {
      this.view.dom.$packageSettingsForm.companyId = companyId;
      this.view.dom.$packageSettingsForm.setPackageData(null);
      this.view.dom.$packageSettingsForm.enableSettingsSection();
    }
  }

  public async getDataForView(): Promise<PackageViewData> {
    let companyId = this.dependencies.getCurrentUserCompany().id;
    if (this.component.formContext === PackageActionContext.Edit) {
      this.packageData = this.component.getPackageData();
      companyId = this.packageData.company.id;
    }

    let userFeatures = this.dependencies.userFeatures;

    if (this.dependencies.isCloud()) {
      userFeatures = await this.dataRepository.getFeaturesByCompany({
        companyId: companyId,
      });
    }
    this.availableFeatures = userFeatures;

    return {
      context: this.component.formContext,
      package: this.packageData ?? null,
      packageType: this.component.getPackageType(),
      availableFeatures: this.availableFeatures,
      panelType: this.component.panelType,
      selectedKitType: this.component.selectedKitType,
    };
  }
}
